import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import {
  subDays,
  subMonths,
  subYears,
  startOfMonth,
  endOfMonth,
  startOfYear,
} from 'date-fns'
import { StyledTextField } from '../styles'
import { formatDate, toDateString } from '@/v2/utils/dateHelpers'

const presetRanges = [
  {
    label: 'Last 7 days',
    getValue: () => ({
      start: toDateString(subDays(new Date(), 7)),
      end: toDateString(new Date()),
    }),
  },
  {
    label: 'Last 30 days',
    getValue: () => ({
      start: toDateString(subDays(new Date(), 30)),
      end: toDateString(new Date()),
    }),
  },
  {
    label: 'Last 90 days',
    getValue: () => ({
      start: toDateString(subDays(new Date(), 90)),
      end: toDateString(new Date()),
    }),
  },
  {
    label: 'Last 6 months',
    getValue: () => ({
      start: toDateString(subMonths(new Date(), 6)),
      end: toDateString(new Date()),
    }),
  },
  {
    label: 'Last year',
    getValue: () => ({
      start: toDateString(subYears(new Date(), 1)),
      end: toDateString(new Date()),
    }),
  },
  {
    label: 'This month',
    getValue: () => ({
      start: toDateString(startOfMonth(new Date())),
      end: toDateString(endOfMonth(new Date())),
    }),
  },
  {
    label: 'Year to date',
    getValue: () => ({
      start: toDateString(startOfYear(new Date())),
      end: toDateString(new Date()),
    }),
  },
]

const DateRangeSelector = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleClick = event => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const handlePresetClick = preset => {
    const { start, end } = preset.getValue()
    onStartDateChange(start)
    onEndDateChange(end)
    handleClose()
  }

  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${formatDate(startDate, 'MMM dd, yyyy')} - ${formatDate(
        endDate,
        'MMM dd, yyyy'
      )}`
    }
    return 'Select date range'
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<CalendarMonthIcon />}
          onClick={handleClick}
          sx={{
            borderRadius: 2,
            borderColor: '#e0e0e0',
            color: '#333',
            textTransform: 'none',
            px: 3,
            py: 1,
            fontSize: '14px',
            fontWeight: 500,
            backgroundColor: 'white',
            '&:hover': {
              borderColor: '#1976d2',
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          {formatDateRange()}
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {[presetRanges[0], presetRanges[1], presetRanges[2]].map(preset => (
            <Button
              key={preset.label}
              size="small"
              onClick={() => handlePresetClick(preset)}
              sx={{
                textTransform: 'none',
                color: '#666',
                fontSize: '13px',
                minWidth: 'auto',
                px: 2,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  color: '#1976d2',
                },
              }}
            >
              {preset.label}
            </Button>
          ))}
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 3, minWidth: 320 }}>
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#333',
                mb: 2,
              }}
            >
              Quick Select
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {presetRanges.map(preset => (
                <Button
                  key={preset.label}
                  fullWidth
                  onClick={() => handlePresetClick(preset)}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    color: '#555',
                    fontSize: '14px',
                    py: 1,
                    px: 2,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      color: '#1976d2',
                    },
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </Box>
          </Box>
          <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 3 }}>
            <Box
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#333',
                mb: 2,
              }}
            >
              Custom Range
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <StyledTextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={e => {
                  const newStartDate = e.target.value
                  onStartDateChange(newStartDate)
                  if (newStartDate && endDate && newStartDate > endDate) {
                    onEndDateChange(newStartDate)
                  }
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
              <StyledTextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={e => {
                  const newEndDate = e.target.value
                  if (!startDate || newEndDate >= startDate) {
                    onEndDateChange(newEndDate)
                  }
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                inputProps={{ min: startDate || undefined }}
                error={endDate && startDate && endDate < startDate}
                helperText={
                  endDate && startDate && endDate < startDate
                    ? 'End date must be after start date'
                    : ''
                }
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleClose}
                sx={{ textTransform: 'none', borderRadius: 1.5, py: 1 }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  )
}

export default DateRangeSelector
